<script setup>
import { Loc, ajax } from 'main.core';
import { computed, nextTick, onMounted, ref, watch } from 'ui.vue3';
import { vMaska } from 'maska/vue';
import { useReCaptcha } from 'vue-recaptcha-v3';
import FormDialog from './FormDialog.vue';
import useCalculatorStore from '../../store/calculator.js';
import useUserStore from '../../store/user.js';

const calculator = useCalculatorStore();
const user = useUserStore();

const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

const configurator = ref(false);
const serviceStorage = ref(false);
const businessId = ref('');
const companyName = ref('');
const fullName = ref('');
const phone = ref('');
const whereDoYouKnow = ref('');
const whereDoYouKnowCustom = ref('');
const comments = ref('');
const offerAccepted = ref(true);
const businessIdError = ref('');
const companyNameError = ref('');
const fullNameError = ref('');
const phoneError = ref('');
const sending = ref(false);
const errors = ref([]);
const sendOk = ref(false);
const whereDoYouKnowCustomInput = ref(null);
const businessIdInput = ref(null);
const companyNameInput = ref(null);
const fullNameInput = ref(null);
const phoneInput = ref(null);

onMounted(() => {
  if (user.fullName) {
    fullName.value = user.fullName;
  }
  if (user.phone) {
    phone.value = user.phone;
  }
  if (user.companyId) {
    businessId.value = user.companyId;
  }
  if (user.companyName) {
    companyName.value = user.companyName;
  }

  if (!phone.value) {
    phoneInput.value.focus({ focusVisible: true });
  }
  if (!fullName.value) {
    fullNameInput.value.focus({ focusVisible: true });
  }
  if (!companyName.value) {
    companyNameInput.value.focus({ focusVisible: true });
  }
  if (!businessId.value) {
    businessIdInput.value.focus({ focusVisible: true });
  }
});

const isCustomWhereDoYouKnow = computed(() => {
  if (whereDoYouKnow.value) {
    const value = calculator.whereDoYouKnow.find(w => w.id === whereDoYouKnow.value);
    if (value) {
      return value.custom;
    }
  }
  return false;
});

const actualWhereDoYouKnow = computed(() => {
  if (whereDoYouKnow.value) {
    const value = calculator.whereDoYouKnow.find(w => w.id === whereDoYouKnow.value);
    if (value) {
      if (value.custom) {
        return whereDoYouKnowCustom.value;
      }
      return value.name;
    }
  }
  return '';
});

watch(isCustomWhereDoYouKnow, async value => {
  if (value) {
    await nextTick();
    if (whereDoYouKnowCustomInput.value) {
      whereDoYouKnowCustomInput.value.focus({ focusVisible: true });
    }
  }
});

const submit = async () => {
  await recaptchaLoaded();
  const googleRecaptchaResponse = await executeRecaptcha('cloud_request_arenda');

  sending.value = true;
  fullNameError.value = '';
  phoneError.value = '';
  companyNameError.value = '';
  businessIdError.value = '';
  errors.value = [];

  try {
    await ajax.runComponentAction('stsl:cloud.calculator', 'arenda', {
      method: 'POST',
      data: {
        program: calculator.selectedProgram,
        users: calculator.selectedUsers,
        bases: calculator.selectedBases,
        full_name: fullName.value,
        phone: phone.value,
        configurator: configurator.value,
        service_storage: serviceStorage.value,
        business_id: businessId.value,
        company_name: companyName.value,
        whereDoYouKnow: actualWhereDoYouKnow.value,
        comments: comments.value,
        offer_accepted: offerAccepted.value,
        'g-recaptcha-response': googleRecaptchaResponse,
      },
    });
    sendOk.value = true;
  } catch (e) {
    console.log(e);
    if (e.errors) {
      for (const error of e.errors) {
        if ('full_name' === error.code) {
          fullNameError.value = error.message;
        } else if ('phone' === error.code) {
          phoneError.value = error.message;
        } else if ('business_id' === error.code) {
          businessIdError.value = error.message;
        } else if ('company_name' === error.code) {
          companyNameError.value = error.message;
        } else {
          errors.value.push(error.message);
        }
      }
    } else {
      errors.value.push(e.message);
    }
  } finally {
    sending.value = false;
  }
};
</script>

<template>
  <FormDialog class="cloud-calculator__dialog_wide">
    <template #title>
      {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_FRESH_TITLE') }}
    </template>
    <div class="alert alert_success" v-if="sendOk">
      <p class="alert-heading">
        {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_FRESH_OK') }}
      </p>
    </div>
    <div class="alert alert_error" style="margin-bottom:16px" v-if="errors.length > 0">
      <p class="alert-heading">
        {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_FRESH_ERROR') }}
      </p>
      <p v-for="error in errors">
        {{ error }}
      </p>
    </div>
    <form method="POST" @submit.prevent="submit" v-if="!sendOk">
      <div class="form-checkbox cloud-calculator__form-item cloud-calculator__form-item_half">
        <input type="checkbox" id="cloud-calculator-arenda-configurator" v-model="configurator">
        <label for="cloud-calculator-arenda-configurator">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_CONFIGURATOR') }}
        </label>
      </div>
      <div class="form-checkbox cloud-calculator__form-item cloud-calculator__form-item_half">
        <input type="checkbox" id="cloud-calculator-arenda-service-storage" v-model="serviceStorage">
        <label for="cloud-calculator-arenda-service-storage">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_SERVICE_STORAGE') }}
        </label>
      </div>
      <div class="form-input cloud-calculator__form-item cloud-calculator__form-item_half">
        <label for="cloud-calculator-arenda-business-id" class="form-input__label">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_BUSINESS_ID') }}:
        </label>
        <input
          ref="businessIdInput"
          type="text"
          id="cloud-calculator-arenda-business-id"
          autocomplete="off"
          class="form-input__input"
          :class="{ 'form-input__input_invalid': businessIdError }"
          :readonly="sending"
          v-model="businessId"
          v-maska="'############'"
        >
        <div class="form-input__error" v-if="businessIdError">
          {{ businessIdError }}
        </div>
      </div>
      <div class="form-input cloud-calculator__form-item cloud-calculator__form-item_half">
        <label for="cloud-calculator-arenda-company-name" class="form-input__label">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_COMPANY_NAME') }}:
        </label>
        <input
          ref="companyNameInput"
          type="text"
          id="cloud-calculator-arenda-company-name"
          autocomplete="off"
          class="form-input__input"
          :class="{ 'form-input__input_invalid': companyNameError }"
          :readonly="sending"
          v-model="companyName"
        >
        <div class="form-input__error" v-if="companyNameError">
          {{ companyNameError }}
        </div>
      </div>
      <div class="form-input cloud-calculator__form-item cloud-calculator__form-item_half">
        <label for="cloud-calculator-arenda-full-name" class="form-input__label">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_FULL_NAME') }}:
        </label>
        <input
          ref="fullNameInput"
          type="text"
          id="cloud-calculator-arenda-full-name"
          autocomplete="name"
          class="form-input__input"
          :class="{ 'form-input__input_invalid': fullNameError }"
          :readonly="sending"
          v-model="fullName"
        >
        <div class="form-input__error" v-if="fullNameError">
          {{ fullNameError }}
        </div>
      </div>
      <div class="form-input cloud-calculator__form-item cloud-calculator__form-item_half">
        <label for="cloud-calculator-arenda-phone" class="form-input__label">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_PHONE') }}:
        </label>
        <input
          ref="phoneInput"
          type="tel"
          id="cloud-calculator-arenda-phone"
          autocomplete="tel"
          class="form-input__input"
          :class="{ 'form-input__input_invalid': phoneError }"
          :readonly="sending"
          v-model="phone"
          v-maska="'+7 (7##) ###-##-##'"
        >
        <div class="form-input__error" v-if="phoneError">
          {{ phoneError }}
        </div>
      </div>
      <div class="form-input cloud-calculator__form-item cloud-calculator__form-item_full">
        <label for="cloud-calculator-arenda-where-do-you-know" class="form-input__label">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_WHERE_DO_YOU_KNOW') }}:
        </label>
        <select
          id="cloud-calculator-arenda-where-do-you-know"
          class="form-select__select"
          :readonly="sending"
          v-model="whereDoYouKnow"
        >
          <option value="">{{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_PLACEHOLDER_NOT_SELECTED') }}</option>
          <option v-for="value in calculator.whereDoYouKnow" :key="value.id" :value="value.id">
            {{ value.name }}
          </option>
        </select>
        <textarea
          ref="whereDoYouKnowCustomInput"
          class="form-input__input"
          style="margin-top:8px"
          v-if="isCustomWhereDoYouKnow"
          v-model="whereDoYouKnowCustom"
          :aria-label="Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_CUSTOM')"
        ></textarea>
      </div>
      <div class="form-input cloud-calculator__form-item cloud-calculator__form-item_full">
        <label for="cloud-calculator-arenda-comments" class="form-input__label">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_COMMENTS') }}:
        </label>
        <textarea id="cloud-calculator-arenda-comments" class="form-input__input" v-model="comments"></textarea>
      </div>
      <div
        class="form-checkbox cloud-calculator__form-item  cloud-calculator__form-item_full"
        v-if="calculator.arendaOfferUrl"
      >
        <input type="checkbox" id="cloud-calculator-arenda-offer-accepted" v-model="offerAccepted">
        <label
          for="cloud-calculator-arenda-offer-accepted"
          v-html="Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_OFFER_ACCEPTED', {
            '#URL#': calculator.arendaOfferUrl,
          })"
        ></label>
      </div>
      <div class="form-input form-input_buttons cloud-calculator__form-item cloud-calculator__form-item_full">
        <button type="submit" class="button" :disabled="sending || !offerAccepted">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_BUTTON_SUBMIT') }}
        </button>
      </div>
      <p class="cloud-calculator__form-item  cloud-calculator__form-item_full">
        <small>{{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_DISCLAIMER_PERSONAL_DATA') }}</small>
      </p>
    </form>
  </FormDialog>
</template>
